const TOOL_META: Record<string, { label: string; icon: string }> = {
  lookup_briefing_slots: {
    label: 'Finding slots',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>`,
  },
  confirm_briefing: {
    label: 'Booking briefing',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>`,
  },
  send_info_email: {
    label: 'Sending info pack',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>`,
  },
  escalate_to_human: {
    label: 'Paging the team',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>`,
  },
  lookup_faq: {
    label: 'Checking the book',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
    </svg>`,
  },
  submit_brief: {
    label: 'Submitting brief',
    icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M22 2 11 13"/>
      <path d="m22 2-7 20-4-9-9-4 20-7z"/>
    </svg>`,
  },
};

interface ActivePill {
  id: string;
  el: HTMLElement;
  hideTimer?: number;
}

/**
 * Tool-call pill — a small glass chip that appears at the top of the
 * stage when Merlin fires a "heavy" tool. Each call produces a pill
 * labelled with the tool name. On tool_done it flips to a green
 * checkmark for a beat, then fades out.
 */
export class ToolPill {
  private readonly root: HTMLElement;
  private readonly active = new Map<string, ActivePill>();

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.classList.add('tool-pill-rail');
  }

  start(id: string, toolName: string) {
    const meta = TOOL_META[toolName] ?? {
      label: toolName.replace(/_/g, ' '),
      icon: '',
    };
    const el = document.createElement('div');
    el.className = 'tool-pill working';
    el.innerHTML = `
      <span class="tool-pill-icon">${meta.icon}</span>
      <span class="tool-pill-label">${meta.label}</span>
      <span class="tool-pill-dots">
        <span></span><span></span><span></span>
      </span>
    `;
    this.root.appendChild(el);
    // force a reflow so the enter transition actually plays
    void el.offsetHeight;
    el.classList.add('enter');
    this.active.set(id, { id, el });
  }

  done(id: string, ok: boolean) {
    const pill = this.active.get(id);
    if (!pill) return;
    pill.el.classList.remove('working');
    pill.el.classList.add(ok ? 'ok' : 'fail');
    const dots = pill.el.querySelector('.tool-pill-dots');
    if (dots) {
      dots.innerHTML = ok
        ? `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`
        : `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
    }
    pill.hideTimer = window.setTimeout(() => {
      pill.el.classList.add('leave');
      window.setTimeout(() => {
        pill.el.remove();
        this.active.delete(id);
      }, 280);
    }, 1200);
  }

  clear() {
    for (const pill of this.active.values()) {
      if (pill.hideTimer) clearTimeout(pill.hideTimer);
      pill.el.remove();
    }
    this.active.clear();
  }
}
